// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useMemo, useState } from 'react';
import { DropdownSearchInput } from './DropdownSearchInput';
import { TagChip } from './TagChip';
import { Spinner } from './Spinner';
import './TagPicker.css';

export interface TagPickerTag {
  id: string;
  name: string;
  color: string;
  archived?: boolean;
}

export interface TagPickerProps {
  /** Full candidate list to search over — non-archived tags only (archived tags reject new assignments, PRD FR-5). */
  tags: TagPickerTag[];
  /** Currently-selected tag ids. */
  selectedIds: string[];
  onToggle: (tagId: string) => void;
  /**
   * Called when the user picks the "Create '<text>'" row. Only rendered
   * when `canCreate` is true and no existing tag case-insensitively
   * matches the typed text exactly (PRD §9 / FR-6).
   */
  onCreate?: (name: string) => void | Promise<void>;
  /** USER role can create tags inline (FR-6); ADMIN always can. Defaults true. */
  canCreate?: boolean;
  loading?: boolean;
  error?: string | null;
  /** True while a create request is in flight — disables the create row. */
  creating?: boolean;
  placeholder?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Type-ahead multi-select tag picker. Renders a search box, the filtered
 * candidate list as togglable rows, and (when enabled) a "Create '<text>'"
 * row when no tag case-insensitively matches the typed text exactly.
 */
export function TagPicker({
  tags,
  selectedIds,
  onToggle,
  onCreate,
  canCreate = true,
  loading = false,
  error = null,
  creating = false,
  placeholder = 'Search or create a tag',
  className = '',
  'data-testid': dataTestId,
}: TagPickerProps) {
  const [search, setSearch] = useState('');

  const trimmedSearch = search.trim();
  const lowerSearch = trimmedSearch.toLowerCase();

  const filtered = useMemo(() => {
    if (!lowerSearch) return tags;
    return tags.filter((t) => t.name.toLowerCase().includes(lowerSearch));
  }, [tags, lowerSearch]);

  const exactMatch = useMemo(
    () => tags.some((t) => t.name.toLowerCase() === lowerSearch),
    [tags, lowerSearch],
  );

  const showCreateRow = canCreate && !!onCreate && trimmedSearch.length > 0 && !exactMatch;

  const handleCreate = () => {
    if (!onCreate || creating) return;
    void onCreate(trimmedSearch);
  };

  return (
    <div className={`fin-tag-picker ${className}`.trim()} data-testid={dataTestId}>
      <DropdownSearchInput
        value={search}
        onChange={setSearch}
        placeholder={placeholder}
        ariaLabel="Search tags"
        data-testid={dataTestId ? `${dataTestId}-search` : undefined}
      />

      {error && <div className="fin-tag-picker__error" role="alert">{error}</div>}

      <div className="fin-tag-picker__list" role="listbox" aria-multiselectable="true">
        {loading ? (
          <div className="fin-tag-picker__loading">
            <Spinner size="sm" display="inline" />
          </div>
        ) : (
          <>
            {filtered.length === 0 && !showCreateRow && (
              <div className="fin-tag-picker__empty">
                {tags.length === 0 && !trimmedSearch
                  ? 'No tags yet — type to create your first tag.'
                  : 'No tags match your search.'}
              </div>
            )}
            {filtered.map((tag) => {
              const checked = selectedIds.includes(tag.id);
              return (
                <label key={tag.id} className="fin-tag-picker__option">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(tag.id)}
                  />
                  <TagChip name={tag.name} color={tag.color} archived={tag.archived} />
                </label>
              );
            })}
            {showCreateRow && (
              <button
                type="button"
                className="fin-tag-picker__create"
                onClick={handleCreate}
                disabled={creating}
              >
                {creating ? <Spinner size="sm" display="inline" /> : `Create "${trimmedSearch}"`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
